import React, { useEffect } from "react";
import { AlertTriangle, X } from "lucide-react";

export default function ConfirmActionModal({
  open,
  title = "Confirmar acción",
  message = "¿Deseas continuar?",
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  tone = "primary", // primary | danger | success
  onConfirm,
  onClose,
  loading = false,
}) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, loading]);

  if (!open) return null;

  return (
    <div className="cam-backdrop" role="dialog" aria-modal="true" aria-labelledby="cam-title">
      <div className="cam-card">
        <button
          type="button"
          className="cam-close"
          onClick={onClose}
          disabled={loading}
          aria-label="Cerrar"
        >
          <X size={18} />
        </button>

        <div className="cam-icon">
          <AlertTriangle size={22} />
        </div>

        <h3 id="cam-title" className="cam-title">
          {title}
        </h3>

        <p className="cam-message">{message}</p>

        <div className="cam-actions">
          <button
            type="button"
            className="cam-btn cam-btn-ghost"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </button>

          <button
            type="button"
            className={`cam-btn cam-btn-${tone}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Enviando..." : confirmText}
          </button>
        </div>
      </div>

      <style>{`
        .cam-backdrop{
          position:fixed;
          inset:0;
          z-index:10000;
          background:rgba(15,23,42,.58);
          backdrop-filter: blur(4px);
          display:grid;
          place-items:center;
          padding:16px;
        }

        .cam-card{
          position:relative;
          width:min(460px, 100%);
          background:#fff;
          border:1px solid #e5e7eb;
          border-radius:22px;
          box-shadow:0 30px 80px rgba(0,0,0,.28);
          padding:22px;
        }

        .cam-close{
          position:absolute;
          top:12px;
          right:12px;
          width:36px;
          height:36px;
          border:none;
          border-radius:10px;
          background:#f8fafc;
          color:#475569;
          cursor:pointer;
          display:grid;
          place-items:center;
        }

        .cam-close:hover{
          background:#eef2f7;
        }

        .cam-icon{
          width:52px;
          height:52px;
          border-radius:16px;
          display:grid;
          place-items:center;
          background:linear-gradient(135deg,#eff6ff,#eef2ff);
          color:#1d4ed8;
          margin-bottom:14px;
        }

        .cam-title{
          margin:0 0 8px;
          font-size:1.15rem;
          font-weight:900;
          color:#0f172a;
          letter-spacing:-.02em;
        }

        .cam-message{
          margin:0;
          color:#475569;
          line-height:1.55;
          font-size:.96rem;
        }

        .cam-actions{
          display:flex;
          justify-content:flex-end;
          gap:10px;
          margin-top:20px;
          flex-wrap:wrap;
        }

        .cam-btn{
          min-width:120px;
          height:44px;
          border-radius:12px;
          border:1px solid #e5e7eb;
          font-weight:800;
          cursor:pointer;
          padding:0 16px;
          transition:.2s ease;
        }

        .cam-btn:hover{
          transform:translateY(-1px);
          box-shadow:0 10px 24px rgba(0,0,0,.08);
        }

        .cam-btn-ghost{
          background:#fff;
          color:#0f172a;
        }

        .cam-btn-primary{
          background:#2563eb;
          border-color:#2563eb;
          color:#fff;
        }

        .cam-btn-danger{
          background:#dc2626;
          border-color:#dc2626;
          color:#fff;
        }

        .cam-btn-success{
          background:#059669;
          border-color:#059669;
          color:#fff;
        }

        @media (max-width: 520px){
          .cam-card{
            padding:18px;
            border-radius:18px;
          }

          .cam-actions{
            flex-direction:column-reverse;
          }

          .cam-btn{
            width:100%;
          }
        }
      `}</style>
    </div>
  );
}